import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../../context/AppState";
import RenderCartDrawerWidget from "./RenderCartDrawerWidget";
import {
  getCartDrawerPlacement,
  getCartDrawerWidgetBounds,
} from "../../utils/cartDrawerRuntime";
import { getCartDrawerTargets } from "../../utils/cartDrawerThemeSelectors";

const PREVIEW_CSS_URL = "https://cdn.weupsell.com/static/css/main.css";

function ensureShadowContent(host, bounds) {
  if (!host) return null;
  const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });

  if (!shadow.querySelector("link[data-weupsell-cart-css]")) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PREVIEW_CSS_URL;
    link.setAttribute("data-weupsell-cart-css", "true");
    shadow.appendChild(link);
  }

  if (!shadow.querySelector("style[data-weupsell-cart-base]")) {
    const style = document.createElement("style");
    style.setAttribute("data-weupsell-cart-base", "true");
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        height: var(--weupsell-cart-drawer-height, auto);
        min-height: var(--weupsell-cart-drawer-height, 120px);
      }
      #weupsell-cart-drawer-content {
        display: block;
        width: 100%;
        max-width: 100%;
        overflow: visible;
      }
    `;
    shadow.appendChild(style);
  }

  let content = shadow.querySelector("#weupsell-cart-drawer-content");
  if (!content) {
    content = document.createElement("div");
    content.id = "weupsell-cart-drawer-content";
    shadow.appendChild(content);
  }

  if (bounds?.height) {
    Object.assign(content.style, {
      width: "100%",
      maxWidth: "100%",
      height: "auto",
      minHeight: "0px",
      boxSizing: "border-box",
      overflow: "visible",
    });
  }

  return content;
}

function buildPopupState(popup) {
  return {
    state: {
      activeStep: 0,
      openPopup: true,
      triggerType: "cart_drawer",
      triggeredAt: Date.now(),
      closedAt: null,
    },
  };
}

function createSlotHost(containerId, placement, tableRow) {
  if (!tableRow) {
    const container = document.createElement("div");
    container.id = containerId;
    container.setAttribute("data-weupsell-cart-drawer-slot", placement);
    Object.assign(container.style, {
      display: "block",
      width: "100%",
      maxWidth: "100%",
      margin: "0",
      padding: "0",
      position: "relative",
      clear: "both",
      boxSizing: "border-box",
    });
    return container;
  }

  const row = document.createElement("tr");
  row.id = `${containerId}-row`;
  row.className = "cart-item weupsell-cart-drawer-row";
  row.setAttribute("data-weupsell-cart-drawer-row", placement);
  Object.assign(row.style, {
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "1fr",
    width: "100%",
    maxWidth: "100%",
  });

  const cell = document.createElement("td");
  cell.colSpan = 99;
  Object.assign(cell.style, {
    padding: "0",
    border: "0",
    display: "block",
    gridColumn: "1 / -1",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  });

  const container = document.createElement("div");
  container.id = containerId;
  container.setAttribute("data-weupsell-cart-drawer-slot", placement);
  Object.assign(container.style, {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    position: "relative",
    clear: "both",
    boxSizing: "border-box",
  });

  container.__weupsellCartDrawerWrapper = row;
  row.appendChild(cell);
  cell.appendChild(container);

  return container;
}

function getInsertElement(container) {
  return container.__weupsellCartDrawerWrapper || container;
}

function removeSlotContainer(container) {
  getInsertElement(container).remove();
}

function getPositiveWidth(element) {
  if (!element) return 0;
  const rect = element.getBoundingClientRect?.();
  const width = rect?.width || element.clientWidth || element.offsetWidth || 0;
  if (!width) return 0;

  if (typeof window !== "undefined" && rect) {
    const visibleWidth = Math.min(
      width,
      window.innerWidth - Math.max(rect.left, 0)
    );
    if (visibleWidth > 0) return visibleWidth;
  }

  return width;
}

function getAvailableSlotWidth(targets, slotConfig) {
  const candidates = [
    targets.widthReference,
    targets.drawer?.querySelector?.(".drawer__inner"),
    targets.drawer?.querySelector?.(".drawer__contents"),
    targets.drawer?.querySelector?.(".drawer__cart-items-wrapper"),
    slotConfig.target,
    targets.drawer,
  ];

  const maxReasonableWidth =
    typeof window !== "undefined" && window.innerWidth > 768
      ? 520
      : Math.max(280, (window.innerWidth || 360) - 32);

  for (const candidate of candidates) {
    const width = getPositiveWidth(candidate);
    if (width > 0 && width <= maxReasonableWidth) {
      return Math.floor(width);
    }
  }

  const fallback = candidates.map(getPositiveWidth).find((width) => width > 0);
  return fallback ? Math.floor(Math.min(fallback, maxReasonableWidth)) : 0;
}

const WeupsellCartDrawerRuntime = ({ popupValues = [] }) => {
  const { appState } = useApp();
  const popups = useMemo(
    () => (Array.isArray(popupValues) ? popupValues.filter(Boolean) : []),
    [popupValues]
  );
  const [portals, setPortals] = useState([]);
  const containersRef = useRef(new Map());

  useEffect(() => {
    if (appState?.is_preview_mode || !popups.length) {
      return undefined;
    }

    let rafId = null;
    let isDisposed = false;

    const removeStaleContainers = (activeIds) => {
      containersRef.current.forEach((container, key) => {
        if (!activeIds.has(key)) {
          removeSlotContainer(container);
          containersRef.current.delete(key);
        }
      });
    };

    const processCartDrawerSlots = () => {
      if (isDisposed) return;

      const targets = getCartDrawerTargets();
      if (!targets) {
        setPortals([]);
        return;
      }

      const nextPortals = [];
      const activeIds = new Set();

      popups.forEach((popup) => {
        if (!popup?._id) return;

        const placement = getCartDrawerPlacement(popup);
        const slotConfig = targets[placement];
        if (!slotConfig?.target) return;

        const containerId = `weupsell-cart-drawer-${popup._id}`;
        activeIds.add(containerId);

        let container = containersRef.current.get(containerId);
        if (!container) {
          container = createSlotHost(containerId, placement, slotConfig.tableRow);
          containersRef.current.set(containerId, container);
        }

        const previousPlacement = container.getAttribute("data-weupsell-cart-drawer-slot");
        if (previousPlacement !== placement) {
          container.setAttribute("data-weupsell-cart-drawer-slot", placement);
          const wrapper = getInsertElement(container);
          wrapper.setAttribute("data-weupsell-cart-drawer-row", placement);
        }

        const bounds = getCartDrawerWidgetBounds(popup);
        const insertElement = getInsertElement(container);
        if (
          previousPlacement !== placement ||
          insertElement.parentElement !==
            (slotConfig.insertPosition === "beforeend"
              ? slotConfig.target
              : slotConfig.target.parentElement)
        ) {
          removeSlotContainer(container);
          slotConfig.target.insertAdjacentElement(slotConfig.insertPosition, insertElement);
        } else if (!insertElement.isConnected) {
          slotConfig.target.insertAdjacentElement(slotConfig.insertPosition, insertElement);
        }

        const availableWidth = getAvailableSlotWidth(targets, slotConfig) || bounds.width;
        const scaledBounds = {
          ...bounds,
          scale: 1,
          availableWidth,
          displayWidth: availableWidth,
          displayHeight: "auto",
        };

        const cell = container.parentElement?.tagName === "TD"
          ? container.parentElement
          : null;
        if (cell) {
          Object.assign(cell.style, {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          });
        }

        Object.assign(container.style, {
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          minHeight: "0px",
          boxSizing: "border-box",
          overflow: "visible",
        });

        const content = ensureShadowContent(container, scaledBounds);
        if (!content) return;

        nextPortals.push({
          id: containerId,
          popup,
          content,
          bounds: scaledBounds,
        });
      });

      removeStaleContainers(activeIds);
      setPortals(nextPortals);
    };

    const scheduleProcess = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processCartDrawerSlots();
      });
    };

    processCartDrawerSlots();

    const observer = new MutationObserver(scheduleProcess);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "open", "aria-hidden"],
    });

    return () => {
      isDisposed = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      observer.disconnect();
      containersRef.current.forEach(removeSlotContainer);
      containersRef.current.clear();
    };
  }, [appState?.is_preview_mode, popups]);

  if (appState?.is_preview_mode || !portals.length) {
    return null;
  }

  return (
    <>
      {portals.map(({ id, popup, content, bounds }) => {
        const popupState = buildPopupState(popup);

        return createPortal(
          <RenderCartDrawerWidget
            popupData={popup}
            popupState={popupState.state}
            setPopupState={() => {}}
            cartDrawerWidgetBounds={bounds}
          />,
          content,
          id
        );
      })}
    </>
  );
};

export default WeupsellCartDrawerRuntime;
